import React, { useState, useMemo } from "react";
import useSignalR from "Infrastructure/Hooks/useSignalR";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import Loader from "Shared/Loaders/Loader";
import { DeviceStatus } from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { BlTabs, TabType } from "Shared/BlTabs";
import InstancesList from "./InstancesList";
import InstanceActionMenu from "./InstanceActionMenu";
import { DeviceLocalization } from "Infrastructure/Models/DeviceLocalizations";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { useDialog } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import {
	HubMessageNotification,
	getAlertColor,
} from "Infrastructure/Models/Messages/MessageNotification";
import {
	Grow,
	Stack,
	Select,
	MenuItem,
	FormControl,
	IconButton,
	InputLabel,
	Badge,
	Popover,
	Typography,
} from "@mui/material";
import { EmptyState } from "Shared/Icons/EmptyState";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import LogController from "Shared/LogWindow/LogController";
import { LoaderDiv } from "Shared/Icons/LoaderDiv";
import { DeviceCategory } from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import ClearIcon from "@mui/icons-material/Clear";
import { BlTooltip } from "Shared/BlTooltip";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import HistoryIcon from "@mui/icons-material/History";
import ActionHistoryDialogContent from "./ActionHistory/ActionHistoryDialogContent";

const InstancesContainer: React.FunctionComponent = () => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [deviceStatuses, setDeviceStatuses] = useState<DeviceStatus[]>([]);
	const [activeTabID, setActiveTabID] = useState<number>(0);
	const [selectedServer, setSelectedServer] = useState<string>("");
	const [selectedCategory, setSelectedCategory] = useState<DeviceCategory | "">(
		""
	);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchorEl);
	const { showDialog } = useDialog();

	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const getDeviceLocalizationByActiveTab = () => {
		switch (activeTabID) {
			case 0:
				return DeviceLocalization.CZ;
			case 1:
				return DeviceLocalization.SK;
			default:
				return DeviceLocalization.CZ;
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: getDeviceLocalizationByActiveTab uses activeTabID
	const devicesByLocalization = useMemo(() => {
		return deviceStatuses.filter(
			(x) => x.deviceLocalization === getDeviceLocalizationByActiveTab()
		);
	}, [deviceStatuses, activeTabID]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getDeviceLocalizationByActiveTab uses activeTabID
	const filteredDeviceStatuses = useMemo(() => {
		let filtered = deviceStatuses;
		if (selectedServer !== "") {
			filtered = filtered.filter((status) =>
				selectedServer === "null"
					? status.deviceServer === null
					: status.deviceServer === selectedServer
			);
		}
		if (selectedCategory !== "") {
			filtered = filtered.filter(
				(status) => status.deviceCategory === selectedCategory
			);
		}
		return filtered.filter(
			(x) => x.deviceLocalization === getDeviceLocalizationByActiveTab()
		);
	}, [deviceStatuses, selectedServer, selectedCategory, activeTabID]);

	const uniqueServers = React.useMemo(() => {
		const servers = deviceStatuses
			.map((status) => status.deviceServer)
			.filter((server, index, self) => self.indexOf(server) === index);
		return servers.sort((a, b) => {
			if (a === null) return 1;
			if (b === null) return -1;
			return a.localeCompare(b);
		});
	}, [deviceStatuses]);

	const onGetDeviceStatusesResponseReceived = async (
		result: DeviceStatus[] | null
	) => {
		if (result) {
			console.log("devices", result);
			updateDeviceStatuses(result);
		}
	};

	const onReceiveServerNotificationMessageReceived = async (
		result: HubMessageNotification
	) => {
		await showSnackbar(result.message, getAlertColor(result.type));
	};

	const onReceiveServerLogMessageReceived = async (
		result: HubMessageNotification
	) => {
		document.dispatchEvent(
			new CustomEvent("addLog", {
				detail: {
					message: "",
					notification: result,
				},
			})
		);
	};

	const debounce = (func: (...args: any[]) => void, delay: number): any => {
		let timeout: NodeJS.Timeout;
		return (...args: any[]) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => func(...args), delay);
		};
	};

	const updateDeviceStatuses = debounce((statuses: DeviceStatus[]): void => {
		setDeviceStatuses(statuses);
	}, 2000);

	const hubConnection = useSignalR(
		`${import.meta.env.VITE_API_URL}/rmq-message-hub`,
		{
			onConnected: (hub) => {
				setIsLoading(false);
				hub.on(
					"getCachedDeviceStatusesResponse",
					onGetDeviceStatusesResponseReceived
				);
				hub.on(
					"receiveServerNotificationMessage",
					onReceiveServerNotificationMessageReceived
				);
				hub.on("receiveServerLogMessage", onReceiveServerLogMessageReceived);
				hub.invoke("getCachedDeviceStatusesRequest").catch((error: Error) => {
					showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
					logError(error);
				});
			},
			onReconnected: (_connectionId) => {
				showSnackbar("Připojeno. Obnovuji...", "success");
				location.reload(); // TODO: Figure out how to invoke a message here instead of doing a page reload
			},
			onDisconnected: (_error) => {
				setIsLoading(false);
				setDeviceStatuses([]);
			},
			onReconnecting: (_error) => {
				setIsLoading(true);
				setDeviceStatuses([]);
				showSnackbar(
					"Odpojeno ze serveru. Pokus o opětovné připojení...",
					"warning"
				);
			},
			onError: (_error) => {
				console.log("error", _error);
				setIsLoading(false);
				setDeviceStatuses([]);
				showSnackbar(
					"Připojení k serveru se nezdařilo. Zkuste to znovu později nebo zkuste aplikaci obnovit.",
					"error"
				);
			},
		}
	);

	const handleClearFilters = () => {
		setSelectedServer("");
		setSelectedCategory("");
	};

	const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleFilterClose = () => {
		setAnchorEl(null);
	};

	const handleShowActionHistoryDialog = async () => {
		await showDialog({
			title: "Historie akcí",
			content: <ActionHistoryDialogContent closeDialog={() => {}} />,
		});
	};

	if (isLoading) {
		return <Loader />;
	}

	const tabs: TabType[] = [
		{
			tabID: 0,
			title: DeviceLocalization.CZ,
			child: (
				<InstancesList
					deviceStatuses={deviceStatuses.filter(
						(x) => x.deviceLocalization === DeviceLocalization.CZ
					)}
					hubConnection={hubConnection}
				/>
			),
			name: "tab-cz-test",
		},
		{
			tabID: 1,
			title: DeviceLocalization.SK,
			child: (
				<InstancesList
					deviceStatuses={deviceStatuses.filter(
						(x) => x.deviceLocalization === DeviceLocalization.SK
					)}
					hubConnection={hubConnection}
				/>
			),
			name: "tab-sk-test",
		},
	];

	return (
		<>
			{!!deviceStatuses && deviceStatuses.length > 0 ? (
				<>
					<Stack
						direction="row"
						spacing={1}
						sx={{
							position: "absolute",
							right: 32,
							zIndex: 1000,
						}}
					>
						<BlTooltip title="Historie akcí">
							<IconButton onClick={handleShowActionHistoryDialog} size="small">
								<HistoryIcon />
							</IconButton>
						</BlTooltip>
						<BlTooltip title="Filtry">
							<IconButton onClick={handleFilterClick} size="small">
								<Badge
									variant="dot"
									invisible={!selectedServer && selectedCategory === ""}
									color="primary"
								>
									<FilterAltIcon />
								</Badge>
							</IconButton>
						</BlTooltip>
						<InstanceActionMenu
							open={true}
							isDisabled={false}
							deviceStatuses={devicesByLocalization}
							hubConnection={hubConnection}
						/>
					</Stack>

					<Popover
						open={open}
						anchorEl={anchorEl}
						onClose={handleFilterClose}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "left",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "left",
						}}
					>
						<Stack spacing={2} sx={{ p: 2, width: "min-content" }}>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								sx={{ minHeight: 32 }}
							>
								<Typography variant="body2" color="text.secondary">
									Filtr
								</Typography>
								{(selectedServer || selectedCategory !== "") && (
									<BlTooltip title="Zrušit filtry">
										<IconButton size="small" onClick={handleClearFilters}>
											<FilterAltOffIcon fontSize="small" />
										</IconButton>
									</BlTooltip>
								)}
							</Stack>
							<FormControl size="small" sx={{ minWidth: 180, width: 180 }}>
								<InputLabel id="category-select-label" shrink={true}>
									Typ
								</InputLabel>
								<Select
									labelId="category-select-label"
									label="Typ"
									value={
										selectedCategory === "" ? "" : Number(selectedCategory)
									}
									onChange={(e) =>
										setSelectedCategory(e.target.value as DeviceCategory | "")
									}
									displayEmpty
								>
									<MenuItem value="">
										<em>Všechny</em>
									</MenuItem>
									<MenuItem value={DeviceCategory.Client}>Klient</MenuItem>
									<MenuItem value={DeviceCategory.Internal}>Interní</MenuItem>
									<MenuItem value={DeviceCategory.Demo}>Demo</MenuItem>
								</Select>
							</FormControl>
							<FormControl size="small" sx={{ minWidth: 180, width: 180 }}>
								<InputLabel id="server-select-label" shrink={true}>
									Server
								</InputLabel>
								<Select
									labelId="server-select-label"
									label="Server"
									value={selectedServer}
									onChange={(e) => setSelectedServer(e.target.value)}
									displayEmpty
								>
									<MenuItem value="">
										<em>Všechny</em>
									</MenuItem>
									{uniqueServers.map((server) => (
										<MenuItem key={server ?? "null"} value={server ?? "null"}>
											{server ?? "Neznámý"}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
					</Popover>
					<BlTabs
						activeTabID={activeTabID}
						tabs={tabs.map((tab) => ({
							...tab,
							child: React.cloneElement(tab.child as React.ReactElement, {
								deviceStatuses: filteredDeviceStatuses,
							}),
						}))}
						onActiveTabChange={(tabID) => setActiveTabID(tabID)}
					/>
				</>
			) : (
				<Grow in={true}>
					<Stack justifyContent="center" alignItems="center">
						<LoaderDiv />
					</Stack>
				</Grow>
			)}

			<LogController />
		</>
	);
};

export default InstancesContainer;
